import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconChevronDown, IconChevronRight, IconSparkles } from '@tabler/icons-react'
import { Formik } from 'formik'
import classNames from 'classnames'
import pluralize from 'pluralize'
import { isEmpty, truncate, capitalize } from 'lodash'

import { Divider } from '@/components/dividers/Divider'

import { Pill } from '@/components/pills/Pill'
import { toast } from '@/components/toasts/toast.helpers'
import {
  setTemporaryCriteria,
  editScorecardConfig,
  setFocusArea,
} from '@/reducers/qa-copilot/qa-copilot.redux'
import { openModal } from '@/reducers/ui/ui.redux'
import { QACopilotScorecardCriteriaPromptForm } from '@/views/QACopilot/forms/QACopilotScorecareCriteriaPromptForm'
import { addPrompt, setPrompts, setTemporaryPrompt } from '@/reducers/prompts/prompts.redux'
import { updatePromptHelper, withoutLegacyModel } from '@/reducers/prompts/prompts.helpers'
import { getStringValueFromBoolean } from '@/utils/helpers'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Button } from '@/components/buttons/Button'

import {
  criteriaTypeOptions,
  createCriteriaHelper,
  updateCriteriaHelper,
  getCriteriaPromptValidationSchema,
  getDefaultFallbackModelId,
  getDefaultQaCopilotModelId,
  normalizeEmailOnFailure,
  splitEmailOnFailure,
} from '../QACopilot.helpers'
import { QACopilotDeleteCriteriaModal } from '../modals/QACopilotDeleteCriteriaModal'

export const QACopilotScorecardCriteriaPrompt = ({
  prompt,
  criteria,
  section,
  isOutdatedScorecard,
}) => {
  const dispatch = useDispatch()
  const { scorecardConfig } = useSelector((state) => state.qaCopilot)
  const {
    prompts,
    modelOptions: {
      qa_copilot: qaCopilotModelOptions = [],
      copilot_fallback: copilotFallbackModelOptions = [],
    } = {},
  } = useSelector((state) => state.prompts)
  const { organizationid: organizationId, prompt_editor: isPromptEditor } = useSelector(
    (state) => state.currentUser
  )
  const isTemporaryCriteria = criteria.is_new && criteria.ssid === section.ssid
  const [editMode, setEditMode] = useState(isTemporaryCriteria)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const criteriaType = criteriaTypeOptions.find((option) => option.value === criteria.criteria_type)
  const criteriaTypeText = criteriaType?.label
  const deleteModalId = `qaCopilot/deleteCriteria-${criteria.scid}`
  const criteriaPointsModalId = 'qaCopilot/criteriaPoints'
  const isBaltoAdmin = organizationId === 1
  const canEditPrompts = isBaltoAdmin || isPromptEditor
  const promptsEnabled =
    canEditPrompts && (criteria.criteria_type === 'ai' || criteria.manual_timestamp_markers)
  const promptTestsEnabled = canEditPrompts && criteria.criteria_type === 'ai'

  const defaultFallbackModelId = useMemo(
    () => getDefaultFallbackModelId(copilotFallbackModelOptions),
    [copilotFallbackModelOptions]
  )
  const defaultQaCopilotModelId = useMemo(
    () => getDefaultQaCopilotModelId(qaCopilotModelOptions),
    [qaCopilotModelOptions]
  )

  const criteriaInitialValues = {
    name: criteria.name || '',
    description: criteria.description || '',
    criteria_type: criteria.criteria_type || 'ai',
    weight: criteria.weight || 1,
    must_be_present: getStringValueFromBoolean(criteria.must_be_present, ''),
    user_prompt: prompt.user_prompt || scorecardConfig.shell_prompt || '',
    first_x_minutes: prompt.first_x_minutes || '',
    last_x_minutes: prompt.last_x_minutes || '',
    side: prompt.side || 'both',
    include_timestamps: getStringValueFromBoolean(prompt.include_timestamps, 'false'),
    silence_threshold_seconds: prompt.silence_threshold_seconds || '',
    // The store resolves a legacy prompt's id from its model name (resolvePromptModelIdsHelper), so the default is
    // only ever a new prompt's. When the name resolves to nothing — a model retired out of the catalog — leave the
    // id unset so the required rule makes someone choose, rather than quietly moving the prompt to the default.
    llm_model_id: prompt.llm_model_id || (isEmpty(prompt.model) ? defaultQaCopilotModelId : null),
    prompt_id: prompt.id || null,
    system_prompt: prompt.system_prompt || '',
    max_response_tokens: prompt.max_response_tokens || null,
    call_id: '',
    expected_response: '',
    test_description: '',
    manual_timestamp_markers: getStringValueFromBoolean(criteria.manual_timestamp_markers, 'false'),
    scores_to_fallback:
      criteria.scores_to_fallback?.map((scoresToFallback) => ({
        id: scoresToFallback,
        label: capitalize(scoresToFallback),
        value: scoresToFallback,
      })) || null,
    fallback_initial_model_id: criteria.fallback_initial_model_id || defaultFallbackModelId,
    tasks_created_on_miss:
      criteria.tasks_created_on_miss?.map((taskType) => ({
        id: taskType,
        label: capitalize(taskType),
        value: taskType,
      })) || [],
    coaching_failure_threshold: criteria.coaching_failure_threshold || 3,
    max_task_evidence: criteria.max_task_evidence || null,
    email_on_failure: criteria.email_on_failure || '',
  }

  const modelTypeDisplayName = qaCopilotModelOptions.find(
    (option) =>
      (prompt.llm_model_id && option.value === prompt.llm_model_id) ||
      (!prompt.llm_model_id && option.name === prompt.model)
  )?.label

  const fallbackModelDisplayName = copilotFallbackModelOptions.find(
    (option) => option.value === criteria.fallback_initial_model_id
  )?.label

  const handleEdit = (focusArea) => {
    setEditMode(true)
    dispatch(setFocusArea(focusArea))
  }

  const handleOpenCriteriaPointsModal = () => {
    dispatch(openModal(criteriaPointsModalId))
  }

  const handleOpenDeleteModal = () => {
    dispatch(openModal(deleteModalId))
  }

  const handleCancel = () => {
    if (isTemporaryCriteria) {
      dispatch(setTemporaryCriteria(null))
      dispatch(setTemporaryPrompt(null))
    } else {
      setEditMode(false)
    }
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt.user_prompt)
    toast.success('Copied!')
  }

  const handleUpdateCriteria = (values) => {
    const newPrompt = withoutLegacyModel({
      ...prompt,
      user_prompt: values.user_prompt || '',
      first_x_minutes: values.first_x_minutes || null,
      last_x_minutes: values.last_x_minutes || null,
      side: values.side,
      include_timestamps: values.include_timestamps === 'true',
      silence_threshold_seconds: values.silence_threshold_seconds || null,
      llm_model_id: values.llm_model_id,
      scid: criteria.scid,
    })

    const newCriteria = {
      ...criteria,
      name: values.name || criteria.name,
      description: values.description || criteria.description,
      criteria_type: values.criteria_type || criteria.criteria_type,
      weight: values.weight || criteria.weight,
      must_be_present: values.must_be_present === 'true',
      manual_timestamp_markers: values.manual_timestamp_markers === 'true',
      scores_to_fallback: !isEmpty(values.scores_to_fallback)
        ? values.scores_to_fallback.map((score) => score.value)
        : null,
      fallback_initial_model_id: !isEmpty(values.scores_to_fallback)
        ? values.fallback_initial_model_id
        : null,
      tasks_created_on_miss: values.tasks_created_on_miss.map((taskType) => taskType.value) || [],
      coaching_failure_threshold: values.coaching_failure_threshold || 3,
      max_task_evidence: values.max_task_evidence,
      email_on_failure: normalizeEmailOnFailure(values.email_on_failure),
    }

    // Whether this criterion runs a prompt once saved — the same condition that renders the model dropdown and
    // drives its validation rule, but read off the values being saved rather than the criterion as it stands. A
    // plain manual criterion runs none, and writing one anyway left an empty prompt in the store for the save to
    // POST.
    const savesPrompt =
      canEditPrompts &&
      (values.criteria_type === 'ai' || values.manual_timestamp_markers === 'true')

    // Only balto admin can edit prompts (prompt fields are only visible to admin anyway)
    if (isTemporaryCriteria) {
      if (savesPrompt) {
        dispatch(addPrompt(newPrompt))
        dispatch(setTemporaryPrompt(null))
      }
      dispatch(
        editScorecardConfig(createCriteriaHelper(scorecardConfig, newCriteria, section.scid))
      )
      dispatch(setTemporaryCriteria(null))
    } else {
      if (savesPrompt) {
        const updatedPrompts = updatePromptHelper(prompts, newPrompt)
        dispatch(setPrompts(updatedPrompts))
      }
      dispatch(editScorecardConfig(updateCriteriaHelper(scorecardConfig, newCriteria)))
      setEditMode(false)
    }
  }

  if (editMode) {
    return (
      <Formik
        initialValues={criteriaInitialValues}
        validationSchema={getCriteriaPromptValidationSchema({ canEditPrompts })}
        onSubmit={handleUpdateCriteria}
        validateOnMount
      >
        {({ handleSubmit, values, errors, setFieldValue }) => {
          return (
            <QACopilotScorecardCriteriaPromptForm
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
              values={values}
              errors={errors}
              isTemporaryCriteria={isTemporaryCriteria}
              setFieldValue={setFieldValue}
              isOutdatedScorecard={isOutdatedScorecard}
              criteria={criteria}
              savedPrompt={prompt}
            />
          )
        }}
      </Formik>
    )
  }

  return (
    <>
      <section
        id={criteria.scid}
        className={classNames('qa-copilot-editor__scorecard-criteria', { collapsed: isCollapsed })}
      >
        <header onClick={() => setIsCollapsed(!isCollapsed)}>
          <div className="flex small-gap vertical-center">
            <Button square basic compact>
              {isCollapsed ? <IconChevronRight /> : <IconChevronDown />}
            </Button>
            <InteractiveTooltip
              interactive
              hide={criteria.name?.length < 60}
              content={criteria.name}
              placement="bottom-start"
            >
              <h2>{truncate(criteria.name, { length: 60 })}</h2>
            </InteractiveTooltip>
          </div>
          <div className="flex gap vertical-center">
            <Pill
              magical={criteriaType?.value === 'ai'}
              informative={criteriaType?.value === 'manual'}
            >
              {criteriaType?.value === 'ai' && (
                <div className="flex primary-color">
                  <IconSparkles className="icon-svg" />
                </div>
              )}
              <div>
                <strong>{criteriaTypeText}</strong>
              </div>
            </Pill>
            <Button
              tooltip="Add points"
              onClick={(event) => {
                event.stopPropagation()

                handleOpenCriteriaPointsModal()
              }}
            >
              {pluralize('Point', criteria.weight, true)}
            </Button>
            <ActionsDropdown
              actions={[
                {
                  label: 'Edit Criterion',
                  disabled: isOutdatedScorecard,
                  onClick: (event) => {
                    event.stopPropagation()

                    handleEdit('')
                  },
                },
                {
                  label: 'Delete Criterion',
                  disabled: isOutdatedScorecard,
                  onClick: (event) => {
                    event.stopPropagation()

                    handleOpenDeleteModal()
                  },
                  isDanger: true,
                },
              ]}
              buttonProps={{ basic: true }}
            />
          </div>
        </header>

        {!isCollapsed && (
          <>
            <div className="qa-copilot-editor__scorecard-criteria--content">
              <div className="qa-copilot-editor__scorecard-criteria--description">
                {criteria.description ? (
                  <p>{criteria.description}</p>
                ) : (
                  <p className="muted-text">
                    No assessment guidelines have been written yet. Edit to add assessment
                    guidelines to this criterion.
                  </p>
                )}
              </div>
              {promptsEnabled && (
                <div className="qa-copilot-editor__scorecard-criteria--ai-prompt copilot-prompt">
                  <div className="header-container">
                    <div className="header-icon">
                      <IconSparkles className="icon-svg" />
                      <h4>{promptTestsEnabled ? 'Copilot Prompt' : 'Timestamp Marker'}</h4>
                    </div>
                    <div className="header-buttons">
                      {promptTestsEnabled && (
                        <Button onClick={() => handleEdit('callid')}>Test Prompt</Button>
                      )}
                      <Button onClick={() => handleEdit('prompt')}>Edit Prompt</Button>
                      <Button brand onClick={handleCopy}>
                        Copy Prompt
                      </Button>
                    </div>
                  </div>
                  {prompt.user_prompt ? (
                    <p>{prompt.user_prompt}</p>
                  ) : (
                    <p className="muted-text">No AI prompt has been written yet.</p>
                  )}
                  <Divider />
                  {criteria.criteria_type === 'ai' && (
                    <p>Passing answer: {criteria.must_be_present ? 'Yes' : 'No'}</p>
                  )}
                  {prompt.first_x_minutes && (
                    <p>
                      Uses{' '}
                      {prompt.first_x_minutes &&
                        `${pluralize(
                          'minute',
                          prompt.first_x_minutes,
                          true
                        )} at the start of the call`}
                    </p>
                  )}
                  {prompt.last_x_minutes && (
                    <p>
                      Uses{' '}
                      {prompt.last_x_minutes &&
                        `${pluralize(
                          'minute',
                          prompt.last_x_minutes,
                          true
                        )} at the end of the call`}
                    </p>
                  )}
                  <p>Uses {prompt.side === 'agent' ? 'agent side only' : 'both sides'}</p>
                  {prompt.include_timestamps && <p>Includes transcript timestamps</p>}
                  {prompt.silence_threshold_seconds && (
                    <p>Flags silences of {prompt.silence_threshold_seconds}s or more</p>
                  )}
                  <p>Uses {modelTypeDisplayName}</p>
                  {criteria.scores_to_fallback && (
                    <p>
                      Run {fallbackModelDisplayName || 'fallback'} first, fallback if{' '}
                      {criteria.scores_to_fallback.join(', ')}
                    </p>
                  )}
                  {criteria.email_on_failure && (
                    <p>Email: {splitEmailOnFailure(criteria.email_on_failure).join(', ')}</p>
                  )}
                </div>
              )}
            </div>
            <footer>
              <Button onClick={() => handleEdit('')}>Edit</Button>
            </footer>
          </>
        )}
      </section>

      <QACopilotDeleteCriteriaModal
        criteria={criteria}
        isTemporaryCriteria={isTemporaryCriteria}
        setEditMode={setEditMode}
      />
    </>
  )
}
